/**
 * **What a picker row's second line says** (VALE-1423).
 *
 * Twenty-one of the catalogue's Respect facilities share the email
 * `finance@respect.com.au`, and the list used to show a favicon and a truncated name
 * and nothing else — so no row said which place it was. A reader looking at
 * `Respect Aged Care Devonport TAS` beside `Respect Aged Care - Devonport TAS` had
 * nothing on screen to tell them apart, or to tell either from the nineteen siblings
 * above them.
 *
 * **It must not invent geography.** There is no suburb, postcode or provider column on
 * `institutions` — location lives inside the name — so the second line carries only
 * what the schema actually holds: the type, the contact email, provenance and date for
 * a row the firm added, and whether Vale holds forms for a catalogue one.
 *
 * **No "Yours" badge.** Provenance belongs in this line and in the group heading, not
 * in a chip on every row: a badge that fires on a whole group is noise, and the group
 * already says it.
 */

/**
 * A short absolute date rather than "3 days ago".
 *
 * The reader is trying to recall a specific occasion — "1 Sep" is a thing you can
 * place, "9 days ago" is arithmetic. Shared with the create step's near-duplicate panel
 * (VALE-1422), which asks the reader the same question about the same rows and must not
 * answer it in a second format.
 */
export function formatInstitutionAddedDate(createdAt: string): string {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return 'date unknown';
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

/**
 * A proximity distance formatted for a reader (VALE-1596).
 *
 * One decimal below 10 km ("2.3 km"), whole kilometres at or above it ("17 km") — the
 * precision a reader can act on differs with the magnitude, and a trailing ".0" on a
 * distant row is noise. Only ever called with a resolved number: a coordinate-less row
 * carries `distanceKm: null` and the caller renders nothing, so this never has to invent
 * a value for one.
 */
export function formatInstitutionDistanceKm(distanceKm: number): string {
  const value =
    distanceKm < 10 ? distanceKm.toFixed(1) : String(Math.round(distanceKm));
  return `${value} km`;
}

/** The fields this module reads. Structural, so tests need no fixtures. */
export interface DescribableInstitution {
  /**
   * **Typed `unknown` because the wire shape and the declared shape disagree, and
   * this module is not the place to reconcile them.** `GET /institutions` runs every
   * row through `populateInstitutionType`, which replaces the slug with the registry's
   * `{ slug, name, … }` object — while every frontend `Institution` interface still
   * declares `type: string`. Narrowing that union at this one call site would ripple a
   * widened `type` through four unrelated consumers for a label on a second line;
   * narrowing it here at runtime costs nothing and claims nothing untrue.
   */
  type?: unknown;
  email?: string | null;
  /** A row this firm added, rather than one from the Vale catalogue. */
  isCustom?: boolean;
  createdAt?: string;
  /** Active institution forms Vale holds. */
  formCount?: number;
}

/**
 * The institution type's human label.
 *
 * `GET /institutions` populates `type` into `{ slug, name, … }` from the type registry,
 * so the label is already the registry's own and is not restated here. A bare slug is
 * accepted because several internal callers still pass one, and is returned unchanged
 * rather than prettified — inventing a label would be a second registry.
 */
function typeLabel(type: unknown): string | null {
  if (!type) return null;
  if (typeof type === 'string') return type;
  if (typeof type === 'object') {
    const populated = type as { name?: unknown; slug?: unknown };
    if (typeof populated.name === 'string') return populated.name;
    if (typeof populated.slug === 'string') return populated.slug;
  }
  return null;
}

/**
 * The facts for one row's second line, in reading order, for the caller to join.
 *
 * Returned as parts rather than a joined string so the caller owns the separator and
 * can drop the line entirely when nothing is known — an empty second line would cost
 * every row its vertical space to say nothing.
 */
export function describeInstitutionRow(
  institution: DescribableInstitution
): string[] {
  const parts: string[] = [];

  if (institution.isCustom) {
    /*
      Provenance leads for a firm's own row, because that is the fact that separates it
      from the catalogue row beside it — and, in the duplicate case this ticket came
      from, from the firm's OWN near-identical row created a day apart. The type is
      dropped here: the reader opened this picker from an asset of that type, so it is
      the one thing on the line they already know.
    */
    parts.push(
      institution.createdAt
        ? `Added by your firm, ${formatInstitutionAddedDate(institution.createdAt)}`
        : 'Added by your firm'
    );
  } else {
    const label = typeLabel(institution.type);
    if (label) parts.push(label);
  }

  if (institution.email) parts.push(institution.email);

  /*
    Forms are a reason to prefer the catalogue row over a firm's own copy, so they are
    only worth saying where they can be held. Silence when there are none: "0 forms" is
    a fact nobody is deciding on.
  */
  if (
    !institution.isCustom &&
    institution.formCount &&
    institution.formCount > 0
  ) {
    parts.push(
      `${institution.formCount} form${institution.formCount === 1 ? '' : 's'}`
    );
  }

  return parts;
}
